import type {SxProps, Theme} from '@mui/material'

export type CriticalityWeight = 'very_low' | 'low' | 'default' | 'high' | 'very_high'

export interface CriticalityOption {
  value: CriticalityWeight
  label: string
  /** Explicit token from the ramp below -- never an MUI semantic colour name. */
  color: string
}

/**
 * Single source of truth for the signal/anomaly/violation criticality scale.
 * Used by the bulk-set modal, the signals grid, the anomaly and violation
 * tables/details AND the production timeline's violation strip, so the
 * value/label/colour mapping is never duplicated (or drifts) across the repo.
 *
 * The colours are explicit tokens rather than MUI semantic names on purpose.
 * The scale used to be `success | info | default | warning | error`, which
 * dragged MUI's *meaning* along with the hue: `success` says "this is good",
 * and nothing on this scale is ever good news -- a Very Low violation came out
 * green, reading as an informational annotation rather than a threshold breach.
 * The ramp below is monotone in severity, cool -> neutral -> warm, with no
 * success green anywhere, so one scale can serve a signal's importance and a
 * violation's severity at once.
 */
export const CRITICALITY_OPTIONS: CriticalityOption[] = [
  {value: 'very_low', label: 'Very Low', color: '#78909c'},
  {value: 'low', label: 'Low', color: '#4dabf5'},
  // `default` means "no weight set", which sorts in the middle of the scale --
  // hence the neutral grey between the cool and warm halves.
  {value: 'default', label: 'Default', color: '#bdbdbd'},
  {value: 'high', label: 'High', color: '#ffa726'},
  {value: 'very_high', label: 'Very High', color: '#f44336'},
]

/**
 * Ink for a filled criticality chip. Every rung of the ramp is light enough
 * that near-black clears 4.5:1 on it, and white does not -- so the ink is fixed
 * rather than theme-dependent, exactly like the chip's background.
 */
export const CRITICALITY_CHIP_INK = '#1c1c1c'

const CRITICALITY_BY_VALUE: Record<string, CriticalityOption> = Object.fromEntries(
  CRITICALITY_OPTIONS.map((option) => [option.value, option]),
)

const DEFAULT_OPTION = CRITICALITY_BY_VALUE.default

/**
 * The ramp colour for one weight. An unknown or missing weight falls back to
 * the neutral `default` rung -- the same thing the backend means when it sends
 * no weight, and the same colour the chip for that row shows.
 */
export const getCriticalityHex = (weight?: string | null): string =>
  (weight ? CRITICALITY_BY_VALUE[weight]?.color : undefined) ?? DEFAULT_OPTION.color

export const getCriticalityLabel = (weight?: string | null): string =>
  (weight ? CRITICALITY_BY_VALUE[weight]?.label : undefined) ?? DEFAULT_OPTION.label

/**
 * Chip styling for one weight. MUI's `color` prop only accepts its own
 * semantic names, which is what put success-green on this scale in the first
 * place, so the ramp is applied through `sx` instead.
 *
 * `outlined` keeps the theme's own text colour and carries the ramp on the
 * border alone: the filled ink is tuned for the ramp's backgrounds, and a
 * mid-tone like amber as *text* fails contrast on a light background.
 */
export const getCriticalityChipSx = (
  weight?: string | null,
  variant: 'filled' | 'outlined' = 'filled',
): SxProps<Theme> => {
  const color = getCriticalityHex(weight)
  return variant === 'outlined'
    ? {bgcolor: 'transparent', color: 'text.primary', border: `1px solid ${color}`}
    : {bgcolor: color, color: CRITICALITY_CHIP_INK, border: '1px solid transparent'}
}
